export type ResolvedDocsLink =
  | { kind: "internal"; href: string }
  | { kind: "anchor"; href: string }
  | { kind: "external"; href: string }
  | { kind: "unavailable"; reason: string };

/**
 * Turns a link as written in the docs markdown into where it goes in the portal. The rules mirror the ones
 * `DocsContentTests` enforces on the content:
 * - `../concepts/conflicts.md#x` — relative to the page's folder; changelog entries resolve from `changelog/`.
 * - `/docs/changelog` — the changelog view.
 * - `/organization/:organizationId/...` — a portal screen for the organization the reader has open.
 */
export const resolveDocsLink = (
  target: string,
  baseSlug: string,
  docsBase: string,
  organizationId?: string,
): ResolvedDocsLink => {
  if (/^(https?:|mailto:)/i.test(target)) return { kind: "external", href: target };
  if (target.startsWith("#")) return { kind: "anchor", href: target };

  const hashIndex = target.indexOf("#");
  const path = hashIndex >= 0 ? target.slice(0, hashIndex) : target;
  const hash = hashIndex >= 0 ? target.slice(hashIndex) : "";

  if (path.startsWith("/docs")) return { kind: "internal", href: `${docsBase}${path.slice("/docs".length)}${hash}` };

  if (path.startsWith("/")) {
    if (!path.includes(":organizationId")) return { kind: "internal", href: `${path}${hash}` };
    if (!organizationId) return { kind: "unavailable", reason: "Open an organization first — this link goes to one of its screens." };
    return { kind: "internal", href: `${path.replace(":organizationId", organizationId)}${hash}` };
  }

  if (!path.endsWith(".md")) return { kind: "external", href: target };

  const segments = baseSlug.split("/").slice(0, -1);
  for (const part of path.slice(0, -3).split("/")) {
    if (part === "..") segments.pop();
    else if (part !== "." && part !== "") segments.push(part);
  }
  const slug = segments.join("/");
  return { kind: "internal", href: `${docsBase}${slug === "index" ? "" : `/${slug}`}${hash}` };
};
